import React, { useState, useEffect } from "react";
import { useTheme } from "@repo/shared";
import { useHistory } from "react-router-dom";

import { confirmAlert } from 'react-confirm-alert'; // Import

import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import BootstrapTable from 'react-bootstrap-table-next';
import filterFactory, { textFilter, dateFilter } from 'react-bootstrap-table2-filter';
import 'react-bootstrap-table2-filter/dist/react-bootstrap-table2-filter.min.css';

import moment from "moment";
import LoadingSpinner from '../common/LoadingSpinner';
import ParentDataService from "../../services/parent/ParentService";
import ConfigDataService from "../../services/config/ConfigService";
import NoData from '../common/NoData';

import { ButtonDelete } from '../common/Buttons';
import SortCaret from '../common/SortCaret';

const ParentsList = (props) => {
  const { appTheme } = useTheme();
  const history = useHistory();
  const [parents, setParents] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTitle, setSearchTitle] = useState("");
  const [deletingParentId, setDeletingParentId] = useState(null);
  const [deletingSubmissionEmail, setDeletingSubmissionEmail] = useState(null);
  const [haveManualVerifications, setHaveManualVerifications] = useState(false);
  useEffect(() => {
    document.title = `${appTheme?.appName || ''} - Parents`;
    retrieveParents();
  }, []);

  const addParent = () => {
    history.push("parent/");
  }

  const openParent = (id) => {
    history.push("parent/" + id);
  };

  const retrieveParents = async () => {
    const r = await ConfigDataService.getConfigByTypeAndName('domain', 'portal');
    console.log(r);
    const verificationDomain = r?.[0]?.value;
    const pl = await ParentDataService.getParents();

    const allParents = [];
    pl?.parents.forEach(p => {
      allParents.push({ ...p, confirmed: true, updateTimestamp: moment(p.updateTimestamp) });
    });

    setHaveManualVerifications(pl?.submissions?.length > 0);
    pl?.submissions.forEach(s => {
      console.log(s);
      const p = JSON.parse(s.json);
      allParents.push({
        ...p.parent,
        verificationUrl: `https://${verificationDomain}/auth/verification?email=${p.parent.email}&code=${s.verificationCode}&source=form`,
        verificationCode: s.verificationCode,
        confirmed: false,
        childCount: p.children ? `${p.children.length}` : '0',
        updateTimestamp: moment(s.updateTimestamp)
      })
    });
    

    allParents.sort((a, b) => {
      if (a.confirmed === false) {
        if (b.confirmed === true) {
          return -1;
        }
      } else {
        if (b.confirmed === false) {
          return 1;
        }
      }
      return b.updateTimestamp.unix() - a.updateTimestamp.unix();
    });
    setParents(allParents);
    setLoading(false);
  };

  const handleDelete = (row) => {
    if (row.confirmed) {
      deleteParent(row.id);
    } else {
      deleteSubmission(row.email, row.verificationCode);
    }
  };
  const deleteSubmission = async (emailAddress, verificationCode) => {
    setDeletingSubmissionEmail(emailAddress);
    try {
      await ParentDataService.deleteSubmission(emailAddress, verificationCode);
      refreshList();
    } catch (error) {
      console.error('Error deleting submission:', error);
      setDeletingSubmissionEmail(null);
    }
  };

  const deleteParent = async id => {
    setDeletingParentId(id);
    try {
      await ParentDataService.deleteParent(id);
      refreshList();
    } catch (error) {
      console.error('Error deleting parent:', error);
      setDeletingParentId(null);
    }
  };

  const refreshList = () => {
    retrieveParents();
  };

  const parentColumns = [
    { sort: true, dataField: 'firstName', text: 'First Name', filter: textFilter(), sortCaret: SortCaret },
    { sort: true, dataField: 'lastName', text: 'Last Name', filter: textFilter(), sortCaret: SortCaret },
    
    { dataField: 'phoneNumber', text: 'Phone Number', filter: textFilter() },
    {
      sort: true, dataField: 'email', text: 'Email', filter: textFilter(), sortCaret: SortCaret,
      formatter: (cell, row) => {
        if (row.confirmed) {
          return (
                <a style={{ color: 'blue' }} onClick={(e) => {
              e.stopPropagation();
              history.push(`/students?emailAddress=${cell}`);
            }}>
              {cell}
            </a>
          )
        } else {
          return (
            <>{cell}</>
          );
        }
      }
    },
    { dataField: 'childCount', text: 'Children', filter: textFilter() },
    {
      sort: true, dataField: 'updateTimestamp', text: 'Updated', filter: dateFilter(), sortCaret: SortCaret,
      formatter: (cell, row) => (
        <>
          {cell.format('MM/DD/YYYY HH:mm')}
        </>
      )
    },
    {
      dataField: 'delete',
      text: '',
      editable: false,
      formatter: (cell, row) => (
        <ButtonDelete 
              onClick={() => handleDelete(row)}
              isDeleting={deletingSubmissionEmail === row.email}
              deleteConfirmationMessage={`Are you sure you want to delete the ${row.confirmed ? 'parent' : 'submission'} with email ${row.email}?`}
              deleteConfirmationTitle={`Delete ${row.confirmed ? 'Parent' : 'Submission'}`}/> 
      ),
      headerStyle: { width: '80px', textAlign: 'center' },
      align: 'center',
    }
  ];
  const [filteredRowCount, setFilteredRowCount] = useState(0);

  function afterFilter(newResult, newFilters) {
    setFilteredRowCount(newResult.length);
  }

  const tableNoDataIndication = () => {
    if (loading) return <div className="p-4"><LoadingSpinner /></div>;
    return <NoData type="parents" />;
  };

  return (
    <Container className="py-2 py-lg-4">
      <Row className="justify-content-center">
        <Col>
          <Card className="h-100 shadow-sm">
            <Card.Header className="d-flex flex-column gap-2">
              <div className="d-flex align-items-center gap-2">
                <h2 className="mb-0">Parents</h2>
                <Button variant="primary" size="sm" onClick={addParent} className="ms-auto">
                  <FontAwesomeIcon icon={faPlus} className="me-2" /> Add Parent
                </Button>
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                Showing <strong>{filteredRowCount}</strong> of <strong>{parents.length}</strong> parents.
              </div>
            </Card.Header>
            <Card.Body className="p-0" style={{ overflowX: 'auto' }}>
              <BootstrapTable
                keyField='id'
                striped
                hover
                condensed
                data={parents}
                columns={parentColumns}
                bordered={false}
                classes="mb-0"
                noDataIndication={tableNoDataIndication}
                filter={filterFactory({ afterFilter })}
                rowEvents={{ onClick: (e, row) => {
                  if (row.confirmed) {
                    openParent(row.id);
                  } else {
                    //popup a confirmAlert to ask if they want to manually confirm the parent
                    confirmAlert({
                      title: 'Manual Verification',
                      message: `Are you sure you want to manually verify for email ${row.email}?`,
                      buttons: [
                        {
                          label: 'Yes',
                          onClick: () => {
                            console.log('verificationUrl is!', row.verificationUrl);
                            window.open(row.verificationUrl, '_blank', 'noopener,noreferrer');
                          }
                        },
                        {
                          label: 'No',
                          onClick: () => { }
                        },
                      ]
                    });
                  }
                } }}
              />
            </Card.Body>
            <Card.Footer className="text-muted">
              Showing <strong>{filteredRowCount}</strong> of <strong>{parents.length}</strong> parents.
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ParentsList;